/** @file VecInput: a labelled row of 2 or 3 numeric fields (the axis letter scrubs), committing the vector. */
import type { ReactElement } from 'react';
import { NumericField } from '../../ui-kit/numericInput/NumericField';
import { NumericRow } from '../../ui-kit/numericInput/NumericRow';

const AXES = ['x', 'y', 'z'] as const;

/**
 * The vector, its label, the unit shown once in the row label, and the commit callback receiving a copy with one
 * component replaced (and that component's index); `axes` names the components (default x y z), `titles` gives each field a
 * hover text. `disabledAxes` disables single components and `reason` is the visible line saying why any are disabled;
 * `onPreview` / `onCancel` show a scrub in progress and undo it.
 */
export type VecInputProps<V extends number[]> = {
  label: string; value: V; onCommit: (value: V, axis: number) => void; unit?: string; step?: number; disabled?: boolean; muted?: boolean;
  axes?: readonly string[]; titles?: readonly string[]; disabledAxes?: readonly boolean[]; reason?: string | null;
  onPreview?: (value: V, axis: number) => void; onCancel?: () => void;
};

/** One `NumericField` per component, labelled `<label> x` / `y` / `z` (or `<label> <axes[i]>`). */
export function VecInput<V extends number[]>(props: VecInputProps<V>): ReactElement {
  const { label, value } = props;
  const axes = props.axes ?? AXES;
  const with_ = (i: number, n: number): V => { const next = [...value] as V; next[i] = n; return next; };
  const { onPreview } = props;
  return (
    <NumericRow label={label} unit={props.unit} reason={props.reason}>
      {value.map((v, i) => (
        <NumericField key={axes[i] ?? i} label={`${label} ${axes[i] ?? String(i)}`} title={props.titles?.[i]} value={v} step={props.step}
          axis={i} handle={(axes[i] ?? '').toUpperCase()} disabled={props.disabled === true || props.disabledAxes?.[i] === true} muted={props.muted}
          onCommit={(n) => { props.onCommit(with_(i, n), i); }}
          onPreview={onPreview === undefined ? undefined : (n) => { onPreview(with_(i, n), i); }} onCancel={props.onCancel} />
      ))}
    </NumericRow>
  );
}
